import { tokens } from '@/design';
import type { ProfileStation } from './fixtures';

export interface ProfileStationsPanelProps {
  readonly stations: ReadonlyArray<ProfileStation>;
  readonly selectedStationId: string | null;
  readonly onSelectStation: (id: string) => void;
}

export function ProfileStationsPanel({
  stations,
  selectedStationId,
  onSelectStation,
}: ProfileStationsPanelProps) {
  return (
    <div
      data-testid="profile-stations-panel"
      style={{
        overflowY: 'auto',
        height: '100%',
        background: tokens.color.surface[1],
        borderRight: tokens.border.thin,
      }}
    >
      <table
        role="table"
        aria-label="Station list"
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: tokens.font.family.mono,
          fontSize: tokens.font.role.engMono.size,
        }}
      >
        <caption
          style={{
            padding: tokens.space[2],
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.size.sm,
            fontWeight: tokens.font.weight.medium,
            textAlign: 'left',
            color: tokens.color.ink.secondary,
          }}
        >
          Station list
        </caption>
        <thead>
          <tr>
            <th scope="col" style={thStyle}>Station</th>
            <th scope="col" style={thStyle}>Chainage</th>
            <th scope="col" style={thStyle}>Elev.</th>
          </tr>
        </thead>
        <tbody>
          {stations.map((station) => (
            <tr
              key={station.id}
              tabIndex={0}
              role="row"
              aria-selected={station.id === selectedStationId}
              onClick={() => onSelectStation(station.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectStation(station.id);
                }
              }}
              style={{
                cursor: 'pointer',
                background:
                  station.id === selectedStationId
                    ? tokens.color.accent.subtle
                    : 'transparent',
                height: tokens.density.rowDefault,
              }}
            >
              <td style={tdStyle}>{station.label}</td>
              <td style={tdStyle}>{station.chainage} m</td>
              <td style={tdStyle}>{station.elevation.toFixed(1)} m</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const thStyle: React.CSSProperties = {
  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
  textAlign: 'left',
  fontWeight: tokens.font.weight.medium,
  fontSize: tokens.font.size.xs,
  color: tokens.color.ink.muted,
  borderBottom: tokens.border.thin,
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
  whiteSpace: 'nowrap',
  color: tokens.color.ink.primary,
};
